import { useState, useCallback, useRef, useEffect, memo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';
import { KanbanColumn } from './column';
import { TaskCard } from './task-card';
import { useUpdateTaskStatus, useReorderTask, useDeleteTask } from '@/hooks/use-tasks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/hooks/use-toast';

interface KanbanBoardProps {
  tasks: any[];
  isLoading?: boolean;
  onTaskClick: (task: any) => void;
  onTaskCreate: () => void;
  highlightedTaskId?: string | null;
}

const columns = [
  { id: 'todo', title: 'To Do', color: 'bg-gray-400' },
  { id: 'in_progress', title: 'In Progress', color: 'bg-blue-400' },
  { id: 'review', title: 'Review', color: 'bg-yellow-400' },
  { id: 'done', title: 'Done', color: 'bg-dyndev-green' },
];

export const KanbanBoard = memo(function KanbanBoard({ tasks, isLoading, onTaskClick, onTaskCreate, highlightedTaskId }: KanbanBoardProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const updateStatus = useUpdateTaskStatus();
  const reorderTask = useReorderTask();
  const deleteTask = useDeleteTask();
  const tasksRef = useRef(tasks);

  // Keep ref in sync without triggering re-renders
  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const getTasksByStatus = (status: string) => {
    return (tasks || [])
      .filter((t: any) => t.status === status)
      .sort((a: any, b: any) => a.sortOrder - b.sortOrder);
  };

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over) return;

      const currentTasks = tasksRef.current;
      const activeTask = currentTasks.find((t: any) => t.id === active.id);
      if (!activeTask) return;

      // Determine the target column and new position
      const overId = over.id as string;
      const isOverColumn = columns.some((c) => c.id === overId);
      const targetStatus = isOverColumn ? overId : currentTasks.find((t: any) => t.id === overId)?.status;

      if (!targetStatus) return;

      try {
        // Status column labels for display
        const statusLabels: Record<string, string> = {
          todo: 'To Do',
          in_progress: 'In Progress',
          review: 'Review',
          done: 'Done',
        };

        // If status changed, update it
        if (activeTask.status !== targetStatus) {
          await updateStatus.mutateAsync({ id: active.id as string, status: targetStatus });
          toast({
            title: 'Task moved',
            description: `"${activeTask.title}" moved to ${statusLabels[targetStatus] || targetStatus}.`,
            variant: 'success',
          });
        }

        // Reorder within the same column
        if (activeTask.status === targetStatus && !isOverColumn) {
          const columnTasks = currentTasks
            .filter((t: any) => t.status === targetStatus)
            .sort((a: any, b: any) => a.sortOrder - b.sortOrder);
          const oldIndex = columnTasks.findIndex((t: any) => t.id === active.id);
          const newIndex = columnTasks.findIndex((t: any) => t.id === over.id);

          if (oldIndex !== -1 && newIndex !== -1) {
            const reordered = arrayMove(columnTasks, oldIndex, newIndex);
            reordered.forEach((task: any, index: number) => {
              if (task.sortOrder !== index) {
                reorderTask.mutate({ id: task.id, sortOrder: index });
              }
            });
          }
        }
      } catch (err: any) {
        toast({ title: 'Failed to move task', description: err?.message || 'An unexpected error occurred.', variant: 'destructive' });
      }
    },
    [updateStatus, reorderTask]
  );

  const [deleteConfirmTask, setDeleteConfirmTask] = useState<any>(null);

  const handleDeleteTask = async (task: any) => {
    setDeleteConfirmTask(task);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmTask) return;

    try {
      await deleteTask.mutateAsync(deleteConfirmTask.id);
      toast({
        title: 'Task deleted',
        description: `"${deleteConfirmTask.title}" has been deleted.`,
        variant: 'success',
      });
    } catch (err: any) {
      toast({
        title: 'Failed to delete task',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setDeleteConfirmTask(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4">
        {columns.map((col) => (
          <div key={col.id} className="flex-1 min-w-[280px] rounded-lg border border-[var(--border-color)] p-4 space-y-3">
            <div className="flex items-center gap-2 mb-3">
              <Skeleton className="h-3 w-3 rounded-full" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-6 rounded-full" />
            </div>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-lg border border-[var(--border-color)] p-3 space-y-2 bg-[var(--card-bg)]">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-1/2" />
                <div className="flex items-center justify-between pt-1">
                  <Skeleton className="h-5 w-14 rounded-full" />
                  <Skeleton className="h-5 w-5 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Kanban Board</h2>
        <button onClick={onTaskCreate} className="btn-dyndev text-sm">
          + Add Task
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto pb-4">
          {columns.map((column) => (
            <KanbanColumn
              key={column.id}
              id={column.id}
              title={column.title}
              color={column.color}
              tasks={getTasksByStatus(column.id)}
              onTaskClick={onTaskClick}
              onTaskDelete={handleDeleteTask}
              highlightedTaskId={highlightedTaskId}
            />
          ))}
        </div>

        <DragOverlay>
          {activeId ? (
            <div className="opacity-80 rotate-2 scale-105">
              <TaskCard
                task={tasks.find((t: any) => t.id === activeId)}
                onClick={() => {}}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteConfirmTask} onOpenChange={(open) => { if (!open) setDeleteConfirmTask(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete task?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirmTask?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
});
